/* global confirm */
import { result } from 'lodash/object';
import React from 'react';

import { showResponseError, showSuccess } from 'utils/messages';
import { scrollTop } from 'utils/wrappers';
import { trimMoney, formatPhone } from 'utils/strings';
import { formatIsoDateUnix } from 'utils/dates';

import FulfillmentOrders from 'app/collections/fulfillment_orders';
import FulfillmentOrder from 'app/models/fulfillment_order';
import FulfillmentOrderReport from 'app/models/fulfillment_order_report';
import ApiModeSelection from 'app/models/api_mode_selection';
import Paginator from 'app/models/paginator';
import DateRange, { DATERANGE_PARAM_TYPES } from 'app/models/date_range';

import Base from 'app/views/base';
import DatepickerRangeView from 'app/views/datepicker_range_view';
import PaginationView from 'app/views/pagination_view';
import SelectorView from 'app/views/selector_view';

import { List as ListView, Heading } from 'app/components/list/list';
import ListSearchView from 'app/components/list/search';
import { DetailBlock } from 'app/components/list/object_details';
import PageHeader from 'app/components/page_header_react';
import RequestReportView from 'app/components/request_report';
import UploadFileView from 'app/components/upload_file_button';

import tmpl from './template';

const STATUS_MAP = {
  created: 'Created',
  preparing: 'Preparing',
  shipped: 'Shipped',
};

const STATUS_CLASS = {
  created: 'muted',
  preparing: 'info',
  shipped: 'success',
};

const PAGE_SIZE = 10;

export default Base.extend({
  compiledTemplate: tmpl,
  className: 'fulfillment_orders',

  initialize() {
    this.apiModeSelection = new ApiModeSelection({});
    this.dateRange = new DateRange({ paramType: DATERANGE_PARAM_TYPES.DATETIME });
    this.collection = new FulfillmentOrders([], {
      dateRange: this.dateRange,
      apiModeSelection: this.apiModeSelection,
    });
    this.paginator = new Paginator({
      collection: this.collection,
      pageSize: PAGE_SIZE,
      buildParams() {
        return {
          limit: PAGE_SIZE,
          page: this.get('currentPage'),
          offset: this.get('currentPage') * PAGE_SIZE,
        };
      },
    });

    this.listenTo(this.collection, 'sync', scrollTop);
    this.listenTo(this.collection, 'update', this.render);

    const headings = [
      new Heading({
        text: m => result(m.get('destination'), 'nameAndCompany'),
        display: 'To',
        xsSize: 7,
        smSize: 4,
        mdSize: 3,
      }),
      new Heading({
        text: m => ({
          class: `badge badge-${STATUS_CLASS[m.get('status')]}`,
          text: STATUS_MAP[m.get('status')],
        }),
        display: 'Status',
        xsSize: 3,
        smSize: 2,
        mdSize: 1,
        wrapperClass: 'p-0',
      }),
      new Heading({
        text: m => trimMoney(m.get('fees').reduce((sum, fee) => fee.amount + sum, 0).toString(), 'USD'),
        display: 'Fees Paid',
        xsSize: 2,
        smSize: 2,
        mdSize: 1,
      }),
      new Heading({
        property: 'latest_delivery',
        display: 'Latest Delivery',
        mdSize: 2,
        smHidden: true,
      }),
      new Heading({
        text: m => formatIsoDateUnix(m.get('updated_at')),
        display: 'Last Updated',
        mdSize: 2,
        smHidden: true,
      }),
      new Heading({
        display: '',
        text: m => {
          if (STATUS_MAP[m.get('status')] === STATUS_MAP.shipped) {
            return { href: '#', text: 'Track package' };
          } else if (STATUS_MAP[m.get('status')] === STATUS_MAP.created) {
            return { class: 'btn btn-xs btn-danger', text: 'Cancel' };
          }
          return '';
        },
        onClick: m => {
          if (STATUS_MAP[m.get('status')] === STATUS_MAP.shipped) {
            window.open(m.get('trackers')[0].public_url);
          } else if (STATUS_MAP[m.get('status')] === STATUS_MAP.created) {
            this.cancelOrder(m);
          }
        },
        xsHidden: true,
        smSize: 2,
        mdSize: 1,
        /* eslint no-nested-ternary: 0 */
        wrapperClass: m => (m ? (STATUS_MAP[m.get('status')] === STATUS_MAP.created ? 'p-0' : '') : ''),
      }),
    ];

    this.addReactComponent('header', <PageHeader name="Fulfillment Orders" />);
    this.addSubview('orderList', '#order_list', new ListView({
      headings,
      collection: this.collection,
      user: this.user,
      noResultsMessage: 'No orders found matching your filter set.',
      showDetails: {
        mainHeading: m => m.get('id'),
        detailBlocks: [
          new DetailBlock({
            heading: 'Status',
            items: m => {
              const status = m.get('status');
              return status ? [STATUS_MAP[status]] : [];
            },
          }),
          new DetailBlock({
            heading: 'Destination',
            items: m => (m.get('destination') ? result(m.get('destination'), 'addressArray') : []),
          }),
          new DetailBlock({
            heading: 'Phone',
            items: m => [formatPhone(m.get('destination').get('phone'))],
          }),
          new DetailBlock({
            heading: 'Tracking Codes',
            items: m => m.get('trackers').map(t => ({ href: t.public_url, text: t.tracking_code })),
          }),
        ],
      },
    }));

    this.addSubview('pagination', '#order_pagination', new PaginationView({ model: this.paginator }));
    this.addSubview('apiModeSelector', '#api_mode_selector', new SelectorView({ model: this.apiModeSelection }));
    this.addSubview('datepickers', '#order_daterange_pickers', new DatepickerRangeView({ model: this.dateRange }));

    this.addSubview('requestReport', '#request_report', new RequestReportView({
      ReportClass: FulfillmentOrderReport,
      dateRange: this.dateRange,
      apiModeSelection: this.apiModeSelection,
    }));

    this.addSubview('uploadOrders', '#upload_orders', new UploadFileView({
      buttonText: 'Upload Orders (CSV)',
      apiModeSelection: this.apiModeSelection,
      callback: options => new FulfillmentOrder().uploadOrdersCSV({
        ...options,
        ...{
          success() {
            showSuccess('Orders accepted. You\'ll receive an email in just a moment.');
          },
        },
      }),
    }));

    this.addSubview('search', '#order_search', new ListSearchView({
      collection: this.collection,
      placeholder: '(Order Id, Tracking Code, or Name)',
      apiModeSelection: this.apiModeSelection,
    }));
  },

  cancelOrder(order) {
    const id = order.get('id');

    if (STATUS_MAP[order.get('status')] !== STATUS_MAP.created) { return; }

    /* eslint no-restricted-globals: 0, no-alert: 0 */
    if (confirm(`Are you sure you want to delete order #${id}? To retrieve it, you will need to contact customer service.`)) {
      order.destroy({
        data: { mode: order.get('mode') },
        emulateJSON: true,
        wait: true,
        success() {
          showSuccess(`Order #${id} deleted.`);
        },
        failure(model, res) {
          showResponseError(res);
        },
      });
    }
  },
});



// WEBPACK FOOTER //
// ./app/components/pages/fulfillment_orders/index.jsx